import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Copy, LogOut, ShieldCheck } from 'lucide-react';
import { Alert, Button, Field, Input, Logo, Spinner } from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';
import { useAuthStore } from '../auth/auth.store';

/** Extrai o segredo base32 do otpauth:// para o admin digitar manualmente (sem câmera). */
function secretFromOtpauth(url: string): string {
  try {
    return new URL(url).searchParams.get('secret') ?? '';
  } catch {
    return '';
  }
}

/**
 * 2FA obrigatório (M1): todo admin sem TOTP habilitado é direcionado pelo
 * AuthGuard para esta tela antes de usar o painel. Reusa /auth/totp/setup +
 * /auth/totp/activate. Anti-lockout: os códigos de recuperação são exibidos
 * aqui e o admin precisa confirmar que salvou antes de habilitar.
 */
export function TwoFactorSetupPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const logout = useAuthStore((s) => s.logout);
  const [code, setCode] = useState('');
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const alreadyEnrolled = user != null && user.requiresTwoFactor;

  const setup = useQuery({
    queryKey: ['totp', 'setup'],
    queryFn: () => api.setupTotp(),
    enabled: user != null && !alreadyEnrolled,
    // gcTime alto: o secret/recovery codes sobrevivem a remounts da rota para o
    // POST /auth/totp/setup rodar só uma vez por sessão (o backend rotaciona o
    // secret a cada chamada enquanto não ativado).
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Se o 2FA já foi habilitado por outro caminho (outra aba/sessão), setupTotp
  // responde ALREADY_ENABLED (409): atualiza o /me e sai da tela em vez de travar.
  useEffect(() => {
    const e = setup.error;
    if (e instanceof PnlApiError && e.api.code === 'ALREADY_ENABLED') {
      void bootstrap().then(() => navigate('/', { replace: true }));
    }
  }, [setup.error, bootstrap, navigate]);

  const activate = useMutation({
    mutationFn: () => api.activateTotp({ code: code.trim() }),
    onSuccess: async () => {
      await bootstrap();
      // Se o /me falhar nesse instante, o store fica 'unauthenticated' — manda
      // pro login (o 2FA já foi ativado no backend) em vez de cair em loop.
      const authed = useAuthStore.getState().status === 'authenticated';
      navigate(authed ? '/' : '/login', { replace: true });
    },
    onError: (e) => {
      if (e instanceof PnlApiError) {
        if (e.api.code === 'INVALID_CODE') {
          setError('Código inválido. Confira no app autenticador e tente novamente.');
        } else if (e.api.code === 'ALREADY_ENABLED') {
          navigate('/', { replace: true });
        } else {
          setError(e.api.message);
        }
      } else {
        setError('Falha ao ativar o 2FA. Tente novamente.');
      }
    },
  });

  // Quem já tem 2FA não precisa (nem deve) refazer o setup.
  if (alreadyEnrolled) return <Navigate to="/" replace />;

  const recoveryCodes = setup.data?.recoveryCodes ?? [];

  function copyRecovery() {
    if (!recoveryCodes.length) return;
    // Copiar é conveniência; os códigos também ficam visíveis/selecionáveis na tela.
    // Não marca "salvei" automaticamente — isso é confirmação consciente do admin.
    navigator.clipboard
      ?.writeText(recoveryCodes.join('\n'))
      .then(() => setCopied(true))
      .catch(() => setError('Não foi possível copiar. Selecione e guarde os códigos manualmente.'));
  }

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    activate.mutate();
  }

  return (
    <div className="bg-ink-50 flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-6">
        <div className="flex justify-center">
          <Logo />
        </div>

        <div className="border-ink-200 shadow-lift rounded-2xl border bg-white p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="bg-brand-50 text-brand-600 flex h-10 w-10 items-center justify-center rounded-xl">
              <ShieldCheck size={20} />
            </div>
            <div>
              <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
                Segurança obrigatória
              </p>
              <h1 className="font-display text-ink-900 text-xl font-semibold tracking-tight">
                Configure a verificação em duas etapas
              </h1>
            </div>
          </div>
          <p className="text-ink-500 mt-3 text-sm">
            O acesso ao painel exige 2FA. Escaneie o QR com um app autenticador (Google
            Authenticator, Authy, 1Password) e confirme com o código gerado.
          </p>

          {error && (
            <div className="mt-4">
              <Alert tone="danger">{error}</Alert>
            </div>
          )}

          {setup.isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : setup.isError ? (
            <div className="mt-4 space-y-4">
              <Alert tone="danger">
                Não foi possível iniciar o setup do 2FA. Recarregue a página e tente novamente.
              </Alert>
              <Button variant="secondary" onClick={() => void setup.refetch()}>
                Tentar de novo
              </Button>
            </div>
          ) : (
            <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
              <div className="flex flex-col items-center gap-3">
                {setup.data?.qrCodeDataUrl && (
                  <img
                    src={setup.data.qrCodeDataUrl}
                    alt="QR code do 2FA"
                    className="border-ink-200 h-44 w-44 rounded-xl border bg-white p-2"
                  />
                )}
                <div className="text-center">
                  <p className="text-ink-500 text-xs">Sem câmera? Digite a chave manualmente:</p>
                  <code className="text-ink-800 bg-ink-100 mt-1 inline-block break-all rounded-md px-2 py-1 font-mono text-xs">
                    {secretFromOtpauth(setup.data?.otpauthUrl ?? '') || '—'}
                  </code>
                </div>
              </div>

              <div className="border-warning-200 bg-warning-50 rounded-xl border p-4">
                <p className="text-warning-800 text-xs font-semibold uppercase tracking-wide">
                  Códigos de recuperação — salve agora
                </p>
                <p className="text-warning-700 mt-1 text-xs">
                  Use um destes códigos se perder o acesso ao app autenticador. Eles aparecem só uma
                  vez.
                </p>
                <div className="mt-3 grid grid-cols-2 gap-1.5">
                  {recoveryCodes.map((rc) => (
                    <code
                      key={rc}
                      className="text-ink-800 rounded bg-white px-2 py-1 text-center font-mono text-xs"
                    >
                      {rc}
                    </code>
                  ))}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="mt-3"
                  leadingIcon={copied ? <Check size={13} /> : <Copy size={13} />}
                  onClick={copyRecovery}
                >
                  {copied ? 'Copiado' : 'Copiar códigos'}
                </Button>
              </div>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={saved}
                  onChange={(e) => setSaved(e.target.checked)}
                  className="text-brand-600 focus:ring-brand-500/30 border-ink-300 mt-0.5 h-4 w-4 rounded"
                />
                <span className="text-ink-600">
                  Guardei os códigos de recuperação num lugar seguro.
                </span>
              </label>

              <Field
                label="Código do app autenticador"
                htmlFor="totp-code"
                required
                hint="6 dígitos, renova a cada 30 segundos."
              >
                <Input
                  id="totp-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  required
                  leadingIcon={<ShieldCheck size={14} />}
                  placeholder="000000"
                />
              </Field>

              <Button
                type="submit"
                block
                size="lg"
                loading={activate.isPending}
                disabled={!saved || code.length !== 6}
              >
                Ativar 2FA e entrar
              </Button>
            </form>
          )}

          <div className="border-ink-100 mt-6 flex items-center justify-center border-t pt-4">
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="text-ink-500 hover:text-ink-700 inline-flex items-center gap-1.5 text-xs font-medium"
            >
              <LogOut size={13} /> Sair e entrar com outra conta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
